/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2021, Bulb Inc. For more information, please email contact@hellobulb.com.
*/
import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { finalize } from 'rxjs';
import { AeroDialogService } from '../../aero/aero-dialog/aero-dialog.service';
import { TranslationService } from '../../common/translation.service';
import { ButtonStyle } from '../../aero/aero-button/aero-button.component';
import { BULB_BUSY_COVER_SERVICE } from '../angularjs.providers';

/**
 * Service responsible for managing and displaying the email verification dialog.
 * Provides a centralized way to trigger the dialog.
 */
@Injectable({
  providedIn: 'root',
})
export class EmailValidationDialogService {

  /**
   * The constructor for the EmailValidationDialogService.
   *
   * @param aeroDialogService - The dialog service for displaying dialogs to the user.
   * @param bulbBrowserService - A service to detect browser-specific properties and behaviors.
   * @param bulbBusyCoverService - A service for managing busy indicators during asynchronous operations.
   * @param $translate - The translation service for fetching localized strings.
   * @param http - The HTTP client service for making HTTP requests.
   */
  constructor(
    private aeroDialogService: AeroDialogService,
    @Inject('BulbBrowserService') private bulbBrowserService : any,
    @Inject(BULB_BUSY_COVER_SERVICE) private bulbBusyCoverService: any,
    private $translate: TranslationService,
    private http: HttpClient,
  ) {
  }

  /**
   * Displays the email verification dialog.
   *
   * @param email - The email address to which the verification email will be sent.
   * @param id - The identifier of the current user associated with the email.
   */
  public show(email: string, id: string) {
    this.openEmailVerificationDialog(email, id);
  }

  /**
   * Show a dialog to notify users that they must have at least one verified email before publishing the page,
   * unless they are a member of an organization or a paid user.
   *
   * @param email - The email address to which the verification email will be sent.
   * @param id - The identifier of the current user associated with the email.
   *
   * @private
   */
  private openEmailVerificationDialog(email: string, id: string) {
    const isMobile = this.bulbBrowserService.isIOSMobile() || this.bulbBrowserService.isAndroid();
    this.aeroDialogService.showDialog({
      svgIconName: 'exclamation-triangle',
      iconClass: 'aero-alert',
      styleClass: 'email-verification',
      title: this.$translate.translateInstant('email_verification_dialog_title'),
      detail: this.$translate.translateInstant('email_verification_dialog_detail'),
      closable: true,
      width: isMobile ? '97%' : '430px',
      showSecondary: false,
      primaryStyle: ButtonStyle.PRIMARY_2,
      primaryLabel: this.$translate.translateInstant('email_verification_dialog_button'),
      onPrimary: () => {
        document.getElementsByTagName('p-dynamicdialog')[0].remove();
        this.sendVerificationEmail(email, id);
      },
      closeOnPrimary: true,
      closeOnEscape: true,
    });
  }

  /**
   * Calls the SendEmailVerification endpoint to resend the verification email.
   * On success, a confirmation dialog is displayed; on failure, an error dialog is shown with the
   * reason for the failure.
   *
   * @param email - The email address to which the verification email will be sent.
   * @param id - The identifier of the current user associated with the email.
   *
   * @private
   */
  private sendVerificationEmail(email: string, id: string) {
    const stopBusyIndicatorCallback = this.bulbBusyCoverService.showBusy();
    this.http.post(`accounts/${id}/sendEmailVerification`, { email })
      .pipe(
        finalize(() => {
          stopBusyIndicatorCallback();
        }),
      )
      .subscribe({
        next: () => {
          this.showEmailSentConfirmation(email);
        },
        error: (error) => {
          this.aeroDialogService.showErrorDialog(error.error?.developerMessage);
        },
      });
  }

  /**
   * Displays a confirmation dialog when the verification email is successfully resent.
   * @param email The email address to which the verification email was resent.
   *
   * @private
   */
  private showEmailSentConfirmation(email: string) {
    this.$translate.translateCustomKeys('account_settings_verify_email_resent', { email }).then((detail) => {
      this.aeroDialogService.showDialog({
        icon: 'fa fa-check-circle',
        title: this.$translate.translateInstant('account_settings_verify_email_sent'),
        detail,
        primaryLabel: this.$translate.translateInstant('button_ok'),
        showSecondary: false,
      });
    });
  }
}
